import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { CheckCircle2, LogOut, Plus, Receipt, Send } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { MoneyAmount } from '@/components/MoneyAmount';
import { RegenerateKeyDialog } from '@/components/RegenerateKeyDialog';
import { useSession } from '@/contexts/SessionContext';
import { ApiError } from '@/lib/fetcher';
import { signOut } from '@/lib/auth';
import {
  accountKey,
  accountLedgerKey,
  createTelegramConnectToken,
  fetchAccount,
  fetchAccountLedger,
} from '@/lib/queries';

// Compact preview window — the full, paginated ledger lives on /dashboard/billing.
const RECENT_LEDGER = { limit: 5, offset: 0 };

export function Account() {
  const navigate = useNavigate();
  const { session, clear } = useSession();
  const q = useQuery({ queryKey: accountKey, queryFn: fetchAccount });
  const ledgerQ = useQuery({
    queryKey: accountLedgerKey(RECENT_LEDGER),
    queryFn: () => fetchAccountLedger(RECENT_LEDGER),
  });

  const onSignOut = async () => {
    clear();
    await signOut();
    navigate('/signin', { replace: true });
  };

  // Brief 258 — Connect Telegram (Courier recipient binding). Mints a deep-link;
  // the human taps it, /start binds their Telegram to this customer. Works for a
  // wallet-signed-in session too (the lodestone path).
  // Brief 263 (258.1 Fix 1) — the deep-link Start button doesn't reliably re-send
  // `/start <token>` for an already-started bot user, so we ALWAYS also surface the
  // raw token + the manual `/start <token>` fallback (handleStart reads the param
  // from message text — the manual path is the same code path, fully working).
  const [tgConnect, setTgConnect] = React.useState<{ deepLink: string; botUsername: string } | null>(null);
  const connectTelegram = useMutation({
    mutationFn: createTelegramConnectToken,
    onSuccess: (data) => {
      setTgConnect({ deepLink: data.deepLink, botUsername: data.botUsername });
      window.open(data.deepLink, '_blank', 'noopener');
    },
  });
  // The token is the part after `start=`; the manual fallback is `/start <token>`.
  const tgToken = tgConnect ? (tgConnect.deepLink.split('start=')[1] ?? '') : '';
  const tgStartCmd = tgToken ? `/start ${tgToken}` : '';
  const [tgCopied, setTgCopied] = React.useState<string | null>(null);
  const copyTg = (label: string, text: string) => {
    void navigator.clipboard?.writeText(text);
    setTgCopied(label);
    window.setTimeout(() => setTgCopied((c) => (c === label ? null : c)), 1500);
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Account</h1>
        <p className="text-muted-foreground">
          Profile, API keys, and session management.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>
            Pulled from your Relaystation customer record.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-72" />
              <Skeleton className="h-5 w-40" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load profile (
                {q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => q.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : q.data ? (
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  Email
                </dt>
                <dd className="font-medium">{q.data.email}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  Name
                </dt>
                <dd className="font-medium">{q.data.name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  Account ID
                </dt>
                <dd className="font-mono text-sm">{q.data.accountId}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  Joined
                </dt>
                <dd>{format(parseISO(q.data.createdAt), 'MMM d, yyyy')}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  Balance
                </dt>
                <dd className="flex items-center gap-3 font-medium">
                  <span className="tabular-nums">
                    {q.data.balanceFormatted ?? q.data.balance}
                  </span>
                  <Button asChild size="sm" variant="outline" className="h-7 gap-1">
                    <Link to="/dashboard/billing">
                      <Plus className="h-3.5 w-3.5" />
                      Add credit
                    </Link>
                  </Button>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  Suspended
                </dt>
                <dd>{q.data.suspended ? 'Yes' : 'No'}</dd>
              </div>
            </dl>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Connect Telegram
          </CardTitle>
          <CardDescription>
            Let your agent reach you on Telegram (Courier <code>notify_operator</code> /{' '}
            <code>ask_operator</code>). You only do this once.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.data?.telegramConnected ? (
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>Telegram is connected. Your agent's messages deliver to your chat.</span>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Not connected yet. Click below to open <code>@relaystation_courier_bot</code> in
                Telegram and tap <strong>Start</strong> — that binds your chat to this account.
              </p>
              <Button
                onClick={() => connectTelegram.mutate()}
                disabled={connectTelegram.isPending}
              >
                {connectTelegram.isPending ? 'Generating link…' : 'Connect Telegram'}
              </Button>
              {connectTelegram.error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    Couldn't create a connect link
                    {connectTelegram.error instanceof ApiError
                      ? ` (${connectTelegram.error.code})`
                      : ''}
                    . Try again.
                  </AlertDescription>
                </Alert>
              )}
              {tgConnect && (
                <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3 text-xs">
                  <p className="text-muted-foreground">
                    If Telegram didn't open,{' '}
                    <a className="underline" href={tgConnect.deepLink} target="_blank" rel="noopener noreferrer">
                      use this link
                    </a>
                    .
                  </p>
                  {/* Fallback path (Brief 263): the deep-link Start button doesn't
                      re-send /start for an already-started bot user. Typing the
                      command manually always works. */}
                  <div className="space-y-1.5">
                    <p className="text-muted-foreground">
                      <strong>If tapping Start does nothing:</strong> open{' '}
                      <code>@{tgConnect.botUsername}</code> in Telegram and send this as a message:
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-background px-2 py-1 font-mono">
                        {tgStartCmd}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 shrink-0"
                        onClick={() => copyTg('cmd', tgStartCmd)}
                      >
                        {tgCopied === 'cmd' ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    <p className="text-muted-foreground">
                      (or just the token: <code className="font-mono">{tgToken}</code>{' '}
                      <button
                        type="button"
                        className="underline"
                        onClick={() => copyTg('token', tgToken)}
                      >
                        {tgCopied === 'token' ? 'copied' : 'copy'}
                      </button>
                      )
                    </p>
                  </div>
                  <p className="text-muted-foreground">
                    The link expires shortly. After connecting, refresh this page.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Recent transactions
            </CardTitle>
            <CardDescription>
              Your latest top-ups and charges. The full ledger lives in Billing.
            </CardDescription>
          </div>
          <Button asChild size="sm" variant="outline" className="shrink-0">
            <Link to="/dashboard/billing">View all</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {ledgerQ.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : ledgerQ.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load transactions (
                {ledgerQ.error instanceof ApiError ? ledgerQ.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => ledgerQ.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : ledgerQ.data && ledgerQ.data.total === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No transactions yet"
              description="Add credit or run a paid call — your entries appear here."
            />
          ) : ledgerQ.data ? (
            <ul className="divide-y">
              {ledgerQ.data.data.map((row) => {
                const credit = row.direction === 'credit';
                return (
                  <li
                    key={row.id}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant={credit ? 'success' : 'secondary'}>
                          {row.direction}
                        </Badge>
                        <span className="truncate text-sm text-muted-foreground">
                          {row.reason || row.serviceKey || row.source}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(row.createdAt).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 font-mono tabular-nums ${
                        credit ? 'text-green-600' : ''
                      }`}
                    >
                      {credit ? '+' : '−'}
                      <MoneyAmount micros={row.amountMicros} minorDigits={4} />
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API keys</CardTitle>
          <CardDescription>
            Use this in <code className="font-mono text-xs">Authorization:
            Bearer &lt;key&gt;</code> on V1 endpoints.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {q.data?.apiKeyPrefix ? (
            <p className="text-sm">
              Active key prefix:{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {q.data.apiKeyPrefix}
              </code>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No active key on this account.
            </p>
          )}
          <RegenerateKeyDialog />
          <p className="text-xs text-muted-foreground">
            Regenerating mints a new key and invalidates the current
            one. The new key is shown once — Relaystation stores only the
            prefix.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Session</CardTitle>
          <CardDescription>
            Sign out clears your session cookie and any wallet JWT
            stored in this tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm">
            Signed in via{' '}
            <span className="font-medium">
              {session?.authType === 'oauth'
                ? 'OAuth'
                : session?.authType === 'wallet'
                  ? 'Wallet JWT'
                  : '—'}
            </span>
          </p>
          <Button variant="destructive" size="sm" onClick={onSignOut} className="gap-2">
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
